import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { ensureToken } from '../../redux/thunk/djpalThunk';
import { fetchFollowedArtists } from '../../redux/thunk/followedArtistsThunk';
import { openMixpediaBrowse } from '../../redux/thunk/appNavThunk';
import { setActiveTab } from '../../redux/slices/appSlice';
import { ReactComponent as FeedIcon } from '../../assets/icons/categories/feed.svg';
import { ReactComponent as ForYouIconV2 } from '../../assets/icons/v2/for-you.svg';
import { playAllTracks } from '../../utils/playAllTracks';
import { entityId } from '../../utils/entityId';
import { useToast } from '../../contexts/ToastContext';
import { isSamplesOnly } from '../../utils/webOnly';
import { useMixPlayback, canPlayMix } from './MixPlayer';
import { resolveMixCover } from '../../utils/nativeMixCover';
import {
    homeRowCache,
    homeRowCacheKey,
    cacheIsFresh,
    peekHomeRowSnapshot,
    peekStaleHomeRow,
    loadHomeRow,
    goMix,
    goMixesPlayingTrack,
    goTrack,
} from './mixpediaHomeData';
import TileScroller from './TileScroller';
import CoverArt from '../CoverArt';
import MixpediaFollowSuggestions from './MixpediaFollowSuggestions';
import { useIsMobileWeb } from '../../hooks/useIsMobileWeb';
import { track as trackEvent } from '../../utils/analytics';
import './MixpediaHome.scss';
import { useDesignV2 } from '../../utils/designV2';
import { ambientHost } from '../../utils/ambientColor';
import { SectionHeader } from '../v2/PageTabs';
import { relativeDate } from '../v2/format';

const FOLLOW_MATURITY = 10;

// Mixpedia landing. Three rows fed by existing state/endpoints:
//   1. Recently followed         — state.followedArtists (newest-first)
//   2. New sets from them        — djpal:latestMixesByArtists (slider row)
//   3. Tracks they played lately — djpal:latestTracksByArtists (slider row)
// Rows 2/3: "Show more" opens the full paged view (homeSets / homeTracks).
const MixpediaHome = () => {
    const dispatch = useDispatch();
    const v2 = useDesignV2(); // DESIGN V2 — section headers, relative dates, neutral glow
    const v2HomeRef = useRef(null);
    // Neutral warm glow on the pane (no artwork lights the home).
    useEffect(() => {
        if (!v2) return undefined;
        const el = ambientHost(v2HomeRef.current);
        if (!el) return undefined;
        el.style.setProperty('--v2-ambient', '150, 125, 100');
        el.style.setProperty('--v2-ambient-2', '90, 95, 110');
        return undefined;
    }, [v2]);
    const store = useStore();

    const followed = useSelector((state) => state.followedArtists.artists);
    const followedLoaded = useSelector((state) => state.followedArtists.loaded);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const userId = useSelector((state) => state.auth.user?.id);

    const { playMix, loadingMixId, playingMixId } = useMixPlayback();
    const toast = useToast();
    const isPlaying = useSelector((state) => state.musicPlayer.isPlaying);
    // The playing-track identity comes from the queue context we set when
    // starting playback (musicPlayer.currentTrack's name is a combined
    // "Title - Artist" string — not reliable for matching row items).
    const listItems = useSelector((state) => state.listContext.items);
    const listIndex = useSelector((state) => state.listContext.currentIndex);
    const nowItem = listIndex >= 0 ? listItems?.[listIndex] : null;
    // Samples-only platforms (web, iOS, Android) can't full-play YT/SC links
    // — a play button gated on those would lie there.
    const trackPlayable = (t) => isSamplesOnly
        ? !!(t.sampleUrl || t.previewUrl || t.preview_url || t.preview)
        : !!(t.sampleUrl || t.previewUrl || t.preview_url || t.preview || t.youtubeId || t.soundcloudUrl);
    const pausePlayback = (e) => {
        e.stopPropagation();
        const a = document.querySelector('audio');
        if (a && !a.paused) a.pause();
    };
    const isSameAsNow = (t) => {
        if (!nowItem) return false;
        const idA = entityId(nowItem);
        const idB = entityId(t);
        if (idA && idB) return idA === idB;
        return nowItem.title === t.title && (nowItem.artist || '') === (t.artist || '');
    };
    const isTrackPlaying = (t) => isPlaying && isSameAsNow(t);
    // Clicking play on the loaded-but-paused item resumes in place instead of
    // re-resolving and restarting from 0:00.
    const resumeIfCurrent = (matches) => {
        if (!matches) return false;
        const a = document.querySelector('audio');
        if (a && a.paused) { a.play().catch(() => {}); return true; }
        return false;
    };
    // Compute the cache key BEFORE state init so a FRESH cache hydrates the
    // first paint synchronously. This matters for Back-navigation: when rows
    // hydrated in an effect (post-paint), the page was near-empty at the
    // moment the tab shell restored scrollTop, the browser clamped it to 0,
    // and Back always landed at the top. The key includes the user id, so a
    // previous login's cache can never leak into the seed (the hazard that
    // used to forbid direct cache seeding).
    // Send the whole follow list (defensive cap only) — a hard 50-cap made
    // big follow sets feel arbitrarily truncated. Server cost is bounded by
    // a recent-window query shape above ~100 follows.
    const followedNames = (followed || []).map(a => a.artist_name).filter(Boolean).slice(0, 500);
    const cacheKey = homeRowCacheKey(userId, followedNames);
    const freshAll = cacheIsFresh('sets', cacheKey) && cacheIsFresh('tracks', cacheKey) && cacheIsFresh('artistTracks', cacheKey);
    // Cold in-memory caches (fresh app launch on web/native): paint last
    // session's persisted rows immediately — stale-while-revalidate, same
    // pattern as the web feed snapshot. The rows effect below still fetches
    // (the cache isn't fresh) and replaces these when real data lands; the
    // per-row skeleton conditions check `items.length === 0`, so seeded rows
    // show content instead of skeletons during that fetch.
    const snapshotRows = freshAll ? null : peekHomeRowSnapshot(cacheKey);
    // Same idea for the in-memory cache past its TTL (the desktop case: the
    // startup prefetch expired while the app sat open, and desktop has no
    // persisted snapshot) — paint the stale rows now, revalidate behind them.
    const seedRow = (kind) => (freshAll
        ? homeRowCache[kind].items
        : (peekStaleHomeRow(kind, cacheKey) || snapshotRows?.[kind]?.items || []));
    const [sets, setSets] = useState(() => seedRow('sets'));
    const [tracks, setTracks] = useState(() => seedRow('tracks'));
    // Row 3: the followed artists' own productions, ranked by recent plays
    // by other DJs ("is their music getting picked up right now").
    const [artistTracks, setArtistTracks] = useState(() => seedRow('artistTracks'));
    const [loadingRows, setLoadingRows] = useState(false);
    // False until the rows effect has produced a result (cache hit, fetch, or
    // nothing-to-fetch). Keeps the suggestions row's hold covering the gap
    // between mount and the effect flipping loadingRows on — otherwise a
    // cached suggestions row could flash in alone on the first frames.
    const [rowsSettled, setRowsSettled] = useState(freshAll);
    const rowsLoadedOnceRef = useRef(freshAll);
    useEffect(() => { if (rowsSettled) rowsLoadedOnceRef.current = true; }, [rowsSettled]);
    // Track-card whose play click is mid-resolve (index) — drives the
    // in-badge spinner during the lazy YouTube/SoundCloud resolution.
    const [resolvingTrackIdx, setResolvingTrackIdx] = useState(null);
    const [resolvingATIdx, setResolvingATIdx] = useState(null);
    const lastKeyRef = useRef(null);
    // Whether this visit's first paint came from the persisted snapshot —
    // reported on mixpedia_home_viewed to measure the snapshot's impact.
    const seededFromSnapshotRef = useRef(!!(snapshotRows && (
        snapshotRows.sets?.items?.length || snapshotRows.tracks?.items?.length || snapshotRows.artistTracks?.items?.length)));
    // ...and whether it came from the stale in-memory cache (desktop's
    // equivalent) — same measurement, separate flag.
    const seededFromStaleRef = useRef(!freshAll && ['sets', 'tracks', 'artistTracks']
        .some(kind => (peekStaleHomeRow(kind, cacheKey) || []).length > 0));

    useEffect(() => {
        if (isAuthenticated && !followedLoaded) dispatch(fetchFollowedArtists());
    }, [dispatch, isAuthenticated, followedLoaded]);

    // One view event per visit, once the rows have settled (counts reflect
    // what the user actually saw, not the pre-fetch empty state).
    const homeViewedRef = useRef(false);
    useEffect(() => {
        if (homeViewedRef.current || !rowsSettled) return;
        homeViewedRef.current = true;
        trackEvent('mixpedia_home_viewed', {
            row_counts: { sets: sets.length, tracks: tracks.length, artist_tracks: artistTracks.length },
            followed_count: (followed || []).length,
            restored_from_snapshot: seededFromSnapshotRef.current,
            restored_from_stale_cache: seededFromStaleRef.current,
        });
    }, [rowsSettled, sets, tracks, artistTracks, followed]);


    useEffect(() => {
        // Wait for the followed-artists fetch before the rows fetch — on cold
        // launch this effect fires while that list is still loading. Rows
        // refetch whenever the followed set changes the cache key, debounced
        // so a burst of follows (Follow all) triggers one refetch, not one
        // per artist.
        if (!isAuthenticated || !followedLoaded) return;
        if (lastKeyRef.current === cacheKey) return;
        const isFirst = lastKeyRef.current === null;

        let cancelled = false;
        const run = async () => {
            lastKeyRef.current = cacheKey;
            if (!followedNames.length) { setSets([]); setTracks([]); setArtistTracks([]); setRowsSettled(true); return; }

            if (cacheIsFresh('sets', cacheKey) && cacheIsFresh('tracks', cacheKey) && cacheIsFresh('artistTracks', cacheKey)) {
                setSets(homeRowCache.sets.items);
                setTracks(homeRowCache.tracks.items);
                setArtistTracks(homeRowCache.artistTracks.items);
                setRowsSettled(true);
                return;
            }

            try {
                setLoadingRows(true);
                const token = await ensureToken(dispatch, store.getState);
                const [setsCache, tracksCache, artistTracksCache] = await Promise.all([
                    loadHomeRow('sets', token, followedNames, cacheKey),
                    loadHomeRow('tracks', token, followedNames, cacheKey),
                    loadHomeRow('artistTracks', token, followedNames, cacheKey),
                ]);
                if (cancelled) return;
                // ts === 0 marks a failed fetch with nothing cached — keep
                // whatever is showing (possibly snapshot-seeded rows) instead
                // of blanking the row on a transient network error.
                if (setsCache.ts) setSets(setsCache.items);
                if (tracksCache.ts) setTracks(tracksCache.items);
                if (artistTracksCache.ts) setArtistTracks(artistTracksCache.items);
            } finally {
                if (!cancelled) { setLoadingRows(false); setRowsSettled(true); }
            }
        };
        const t = setTimeout(run, isFirst ? 0 : 2500);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, followedLoaded, cacheKey, dispatch, store]);

    // Suggestions-row "synced reveal": hold its skeleton only while the
    // content rows are genuinely empty and loading. When they're already
    // painted (fresh cache, stale-while-revalidate, snapshot), holding would
    // blank a row that has content — the opposite of the intent.
    const rowsSeeded = sets.length > 0 || tracks.length > 0 || artistTracks.length > 0;
    const holdSuggestions = isAuthenticated && !rowsSeeded && (loadingRows || !rowsSettled);

    // Cold start (no follows): show the follow-suggestions view instead of a
    // blank page — suggestions stay visible until a few follows accumulate.
    const showSuggestions = followedLoaded && followed.length < 3;
    // Hybrid ordering: while the taste graph is young, growing follows is the
    // page's most valuable job → suggestions row leads. Once follows mature,
    // content leads and the row sinks to the bottom (and disappears entirely
    // when the library is fully followed).
    const suggestionsFirst = followed.length < FOLLOW_MATURITY;
    // The "From your library — not following yet" row is desktop-only: on
    // mobile the home should stay focused on content rows (the cold-start
    // suggestions grid for users with <3 follows is kept on both).
    const isMobileWeb = useIsMobileWeb();
    const showSuggestionsRow = !showSuggestions && !isMobileWeb;

    // Row 3 dedupe: when a followed artist plays another followed artist's
    // production, the track can appear in both rows — keep it in row 2 only.
    const nameKey = (t) => `${(t.artist || '').toLowerCase()}|${(t.title || '').toLowerCase()}`;
    const tracksKeySet = new Set(tracks.map(nameKey));
    const artistTracksVisible = artistTracks.filter(t => !tracksKeySet.has(nameKey(t)));

    // Cover peek for the For You banner: a taste of artwork pulled from the
    // already-fetched rows (no extra fetch) so the full-width banner's right
    // side isn't dead space.
    const peekCovers = [...sets, ...tracks]
        .map((x) => resolveMixCover(x) || x.artworkUrl)
        .filter(Boolean)
        .slice(0, 4);

    return (
        <div className={`mixpedia-home${v2 ? ' v2-ambient v2-home' : ''}`} ref={v2HomeRef}>
            {followedLoaded && followed.length === 0 && (
                <div className="mh-cold-hero">
                    <h2 className="mh-cold-title">Welcome to <span className="mh-cold-brand">Mixpedia</span></h2>
                    <p className="mh-cold-sub">Follow artists you play — their newest sets and tracks will land here.</p>
                </div>
            )}
            {/* Shortcut to the For You feed — the sibling discovery surface.
                Spotify-style wayfinding tile: top of page, where the "what
                now" decision happens. Kept above the follow-suggestions grid
                so a large "artists from your library" list (100s of rows)
                can't scroll it out of reach. Skeleton while the rows load so
                the whole page reveals together (same pact as the other
                rows). Desktop-only: mobile's bottom nav already has the For
                You quick link, so the banner would be a duplicate doorway. */}
            {isSamplesOnly ? null : loadingRows && sets.length === 0 && tracks.length === 0 ? (
                <div className="mh-shortcut-tile mh-skeleton-card">
                    <span className="mh-skeleton-avatar mh-shortcut-skel-icon" />
                    <span className="mh-shortcut-text">
                        <span className="mh-skeleton-line mh-shortcut-skel-line" />
                        <span className="mh-skeleton-line short mh-shortcut-skel-line" />
                    </span>
                </div>
            ) : (
                <button className="mh-shortcut-tile" onClick={() => dispatch(setActiveTab('feed'))}>
                    <span className="mh-shortcut-icon">{v2 ? <ForYouIconV2 /> : <FeedIcon />}</span>
                    <span className="mh-shortcut-text">
                        <span className="mh-shortcut-title">For You</span>
                        <span className="mh-shortcut-sub">Fresh music picked for you</span>
                    </span>
                    {peekCovers.length > 0 && (
                        <span className="mh-shortcut-peek">
                            {peekCovers.map((src, i) => <CoverArt key={i} src={src} size={26} eager />)}
                        </span>
                    )}
                    <span className="mh-shortcut-chevron">&rsaquo;</span>
                </button>
            )}

            {showSuggestions && <MixpediaFollowSuggestions />}
            {showSuggestionsRow && suggestionsFirst && <MixpediaFollowSuggestions variant="row" hold={holdSuggestions} />}

            {(sets.length > 0 || loadingRows) && (
                <section className="mh-section">
                    {v2 ? <SectionHeader title="New mixes from artists you follow" link={(sets.length > 0) ? 'See all' : null} onLink={() => dispatch(openMixpediaBrowse('homeSets'))} /> : (
<div className="mh-section-header">
                        <h3 className="mh-title">New sets from artists you follow</h3>
                        {sets.length > 0 && (
                            <button className="mh-show-more" onClick={() => dispatch(openMixpediaBrowse('homeSets'))}>
                                Show more
                            </button>
                        )}
                    </div>
                    )}
                    {loadingRows && sets.length === 0 ? (
                        <div className="mh-slider-row mh-skeleton-row">
                            {Array.from({ length: 6 }, (_, i) => (
                                <div key={i} className="mh-mix-card mh-skeleton-card">
                                    <div className="mh-skeleton-art" />
                                    <div className="mh-skeleton-line" />
                                    <div className="mh-skeleton-line short" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <TileScroller className="mh-tile-row">
                            {sets.map((m, i) => (
                                <div key={`${m.id || m.title}-${i}`} className="mh-mix-card" onClick={() => goMix(dispatch, m, 'home_row_sets')} role="button" tabIndex={0}>
                                    <div className="mh-mix-art-wrap">
                                        {resolveMixCover(m) || m.artworkUrl ? (
                                            <CoverArt src={resolveMixCover(m) || m.artworkUrl} size={130} className="mh-mix-art" />
                                        ) : (
                                            <span className="mh-mix-art is-empty">&#9835;</span>
                                        )}
                                        {playingMixId === m.id && isPlaying ? (
                                            <button className="mh-hover-play playing" title="Pause" onClick={pausePlayback}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                            </button>
                                        ) : (!isSamplesOnly && canPlayMix(m) && m.link && (
                                            <button
                                                className="mh-hover-play"
                                                title="Play"
                                                onClick={(e) => { e.stopPropagation(); if (resumeIfCurrent(playingMixId === m.id)) return; playMix(m, { items: sets, index: i }); }}
                                            >
                                                {loadingMixId === m.id
                                                    ? <span className="mix-play-spinner" />
                                                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>}
                                            </button>
                                        ))}
                                    </div>
                                    <span className="mh-mix-title">{m.title || m.name}</span>
                                    <span className="mh-mix-sub">
                                        {(m.djName || m.artist || '')}{m.date ? ` · ${v2 ? relativeDate(m.date) : String(m.date).slice(0, 10)}` : ''}
                                    </span>
                                </div>
                            ))}
                        </TileScroller>
                    )}
                </section>
            )}

            {(tracks.length > 0 || loadingRows) && (
                <section className="mh-section">
                    {v2 ? <SectionHeader title="Tracks played recently by artists you follow" link={(tracks.length > 0) ? 'See all' : null} onLink={() => dispatch(openMixpediaBrowse('homeTracks'))} /> : (
<div className="mh-section-header">
                        <h3 className="mh-title">Tracks played recently by artists you follow</h3>
                        {tracks.length > 0 && (
                            <button className="mh-show-more" onClick={() => dispatch(openMixpediaBrowse('homeTracks'))}>
                                Show more
                            </button>
                        )}
                    </div>
                    )}
                    {loadingRows && tracks.length === 0 ? (
                        <div className="mh-slider-row mh-skeleton-row">
                            {Array.from({ length: 6 }, (_, i) => (
                                <div key={i} className="mh-mix-card mh-skeleton-card">
                                    <div className="mh-skeleton-art" />
                                    <div className="mh-skeleton-line" />
                                    <div className="mh-skeleton-line short" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <TileScroller className="mh-tile-row">
                            {tracks.map((t, i) => (
                                <div key={`${t.id || t.title}-${i}`} className="mh-mix-card" onClick={() => goTrack(dispatch, t, 'home_row_tracks')} role="button" tabIndex={0}>
                                    <div className="mh-mix-art-wrap">
                                        {t.artworkUrl ? (
                                            <CoverArt src={t.artworkUrl} size={130} className="mh-mix-art" />
                                        ) : (
                                            <span className="mh-mix-art is-empty">&#9835;</span>
                                        )}
                                        {isTrackPlaying(t) ? (
                                            <button className="mh-hover-play playing" title="Pause" onClick={pausePlayback}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                            </button>
                                        ) : (trackPlayable(t) && (
                                            <button
                                                className="mh-hover-play"
                                                title="Play"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (resumeIfCurrent(isSameAsNow(t))) return;
                                                    if (resolvingTrackIdx !== null) return;
                                                    setResolvingTrackIdx(i);
                                                    try {
                                                        const ok = await playAllTracks(dispatch, tracks, i);
                                                        if (!ok) toast.info("Couldn't find a playable source for this track");
                                                    }
                                                    finally { setResolvingTrackIdx(null); }
                                                }}
                                            >
                                                {resolvingTrackIdx === i
                                                    ? <span className="mix-play-spinner" />
                                                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>}
                                            </button>
                                        ))}
                                    </div>
                                    <span className="mh-mix-title">{t.title}</span>
                                    <span className="mh-mix-sub">{t.artist || ''}</span>
                                    {t.mixDj && (
                                        <span
                                            className="mh-sub-badge mh-sub-badge-link"
                                            title={`Open ${t.mixDj}'s set${t.mixTitle ? ` — ${t.mixTitle}` : ''}`}
                                            onClick={(e) => { e.stopPropagation(); goMix(dispatch, { id: t.mixId, publicId: t.mixPublicId, djName: t.mixDj, title: t.mixTitle }, 'home_row_tracks'); }}
                                        >
                                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/></svg>
                                            <span className="mh-sub-badge-name">{t.mixDj}</span>
                                        </span>
                                    )}
                                </div>
                            ))}
                        </TileScroller>
                    )}
                </section>
            )}
            {(artistTracksVisible.length > 0 || loadingRows) && (
                <section className="mh-section">
                    {v2 ? <SectionHeader title="Tracks by artists you follow, played by other DJs" link={(artistTracksVisible.length > 0) ? 'See all' : null} onLink={() => dispatch(openMixpediaBrowse('homeArtistTracks'))} /> : (
<div className="mh-section-header">
                        <h3 className="mh-title">Tracks by artists you follow, played by other DJs</h3>
                        {artistTracksVisible.length > 0 && (
                            <button className="mh-show-more" onClick={() => dispatch(openMixpediaBrowse('homeArtistTracks'))}>
                                Show more
                            </button>
                        )}
                    </div>
                    )}
                    {/* Skeleton whenever it is loading with nothing to show — after the
                        first load an empty row used to render as a blank strip. */}
                    {loadingRows && artistTracksVisible.length === 0 ? (
                        <div className="mh-slider-row mh-skeleton-row">
                            {Array.from({ length: 6 }, (_, i) => (
                                <div key={i} className="mh-mix-card mh-skeleton-card">
                                    <div className="mh-skeleton-art" />
                                    <div className="mh-skeleton-line" />
                                    <div className="mh-skeleton-line short" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <TileScroller className="mh-tile-row">
                            {artistTracksVisible.map((t, i) => (
                                <div key={`${entityId(t) || t.title}-${i}`} className="mh-mix-card" onClick={() => goTrack(dispatch, t, 'home_row_artist_tracks')} role="button" tabIndex={0}>
                                    <div className="mh-mix-art-wrap">
                                        {t.artworkUrl ? (
                                            <CoverArt src={t.artworkUrl} size={130} className="mh-mix-art" />
                                        ) : (
                                            <span className="mh-mix-art is-empty">&#9835;</span>
                                        )}
                                        {isTrackPlaying(t) ? (
                                            <button className="mh-hover-play playing" title="Pause" onClick={pausePlayback}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                            </button>
                                        ) : (trackPlayable(t) && (
                                            <button
                                                className="mh-hover-play"
                                                title="Play"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (resumeIfCurrent(isSameAsNow(t))) return;
                                                    if (resolvingATIdx !== null) return;
                                                    setResolvingATIdx(i);
                                                    try {
                                                        const ok = await playAllTracks(dispatch, artistTracksVisible, i);
                                                        if (!ok) toast.info("Couldn't find a playable source for this track");
                                                    }
                                                    finally { setResolvingATIdx(null); }
                                                }}
                                            >
                                                {resolvingATIdx === i
                                                    ? <span className="mix-play-spinner" />
                                                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>}
                                            </button>
                                        ))}
                                    </div>
                                    <span className="mh-mix-title">{t.title}</span>
                                    <span className="mh-mix-sub">{t.artist || ''}</span>
                                    {t.recentPlays > 0 && (
                                        <span
                                            className="mh-sub-badge mh-sub-badge-link"
                                            title="See the sets that played this"
                                            onClick={(e) => { e.stopPropagation(); goMixesPlayingTrack(dispatch, t, homeRowCache.artistTracks.meta?.windowDays || 90); }}
                                        >
                                            <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>
                                            <span className="mh-sub-badge-name">{t.recentPlays} {t.recentPlays === 1 ? 'play' : 'plays'}</span>
                                        </span>
                                    )}
                                </div>
                            ))}
                        </TileScroller>
                    )}
                </section>
            )}
            {showSuggestionsRow && !suggestionsFirst && <MixpediaFollowSuggestions variant="row" hold={holdSuggestions} />}
        </div>
    );
};

export default MixpediaHome;
